import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Mail,
  Save,
  Send,
  Plus,
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  Users,
  CalendarClock,
  Search,
  Globe2,
  RefreshCw,
  Info,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { Breadcrumb } from '../../components/Common'
import emailNotificationService, { describeError } from '../../services/emailNotificationService'
import ManagementEmailSettingsPanel from './ManagementEmailSettingsPanel'
import GstComplianceEmailPanel from './GstComplianceEmailPanel'
import '../../styles/email-notifications.css'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const TEAM_SUGGESTION_LIMIT = 12

const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'America/New_York',
  'UTC',
]

const CATEGORY_LABELS = {
  Sales: 'Sales',
  Expenses: 'Expenses',
  Projects: 'Projects',
  Assets: 'Assets',
  Timesheets: 'Timesheets',
  Contracts: 'Contracts',
  Scheduled: 'Daily digest',
  'Management MIS': 'Management MIS',
  'GST Compliance': 'GST Compliance',
}

const LEAD_TIME_FIELDS = [
  { key: 'staleDays', label: 'Idle quotations', suffix: 'days without update', min: 1, max: 365 },
  { key: 'meetingDaysBefore', label: 'Asset maintenance', suffix: 'days ahead', min: 0, max: 30 },
  { key: 'taskDaysBefore', label: 'Project tasks', suffix: 'days ahead', min: 0, max: 30 },
  { key: 'oppCloseDaysBefore', label: 'Quotation expiry', suffix: 'days ahead', min: 0, max: 60 },
]

const EMPTY_FORM = {
  enabled: true,
  toEmails: [],
  ccEmails: [],
  bccEmails: [],
  toUserIds: [],
  ccUserIds: [],
  bccUserIds: [],
}

const DEFAULT_SCHEDULE = {
  timezone: 'Asia/Kolkata',
  sendTime: '08:00',
  leadTimes: { staleDays: 10, meetingDaysBefore: 1, taskDaysBefore: 0, oppCloseDaysBefore: 3 },
  digestEnabled: true,
  senderDisplayName: 'Celeris ERP Notifications',
  senderEmail: '',
  replyToName: '',
  replyToEmail: '',
  hideZeroCategories: true,
  supportContact: '',
  scheduledEvents: [],
  lastDigestAt: null,
  lastDigestStatus: null,
  lastDigestError: null,
}

function categoryLabel(cat) {
  return CATEGORY_LABELS[cat] || cat || 'Other'
}

function normalizeList(arr) {
  const seen = new Set()
  const out = []
  for (const raw of arr || []) {
    const e = String(raw || '').trim().toLowerCase()
    if (!EMAIL_RE.test(e) || seen.has(e)) continue
    seen.add(e)
    out.push(e)
  }
  return out
}

function formFromEvent(ev) {
  if (!ev) return EMPTY_FORM
  return {
    enabled: ev.enabled !== false,
    toEmails: normalizeList(ev.toEmails),
    ccEmails: normalizeList(ev.ccEmails),
    bccEmails: normalizeList(ev.bccEmails),
    toUserIds: (ev.toUserIds || []).map(String),
    ccUserIds: (ev.ccUserIds || []).map(String),
    bccUserIds: (ev.bccUserIds || []).map(String),
  }
}

function sameForm(a, b) {
  const key = (f) => JSON.stringify([
    f.enabled,
    [...f.toEmails].sort(), [...f.ccEmails].sort(), [...f.bccEmails].sort(),
    [...f.toUserIds].sort(), [...f.ccUserIds].sort(), [...f.bccUserIds].sort(),
  ])
  return key(a) === key(b)
}

function eventMatchesQuery(ev, q) {
  if (!q) return true
  const hay = [ev.label, ev.description, ev.category, ev.eventKey].filter(Boolean).join(' ').toLowerCase()
  return q.split(/\s+/).every((token) => hay.includes(token))
}

function recipientCount(ev) {
  return (ev.toEmails?.length || 0) + (ev.ccEmails?.length || 0) + (ev.bccEmails?.length || 0)
}

function formatDateTime(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function reportError(err, fallback) {
  const { message, handled } = describeError(err, fallback)
  if (!handled) toast.error(message)
}

export function EmailChipList({ label, hint, emails, onChange, onPickUser, disabled, users }) {
  const [draft, setDraft] = useState('')

  const addEmail = (value) => {
    const e = String(value || '').trim().toLowerCase()
    if (!e) return
    if (!EMAIL_RE.test(e)) {
      toast.error(`Invalid email: ${e}`)
      return
    }
    const teamUser = users.find((u) => u.email === e)
    if (teamUser) onPickUser(teamUser)
    else onChange(normalizeList([...emails, e]))
    setDraft('')
  }

  const removeEmail = (email) => onChange(emails.filter((x) => x !== email))

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || (e.key === 'Tab' && draft.trim())) {
      e.preventDefault()
      addEmail(draft)
    } else if (e.key === 'Backspace' && !draft && emails.length) {
      removeEmail(emails[emails.length - 1])
    }
  }

  const nameByEmail = useMemo(() => new Map(users.map((u) => [u.email, u.name])), [users])

  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase()
    return users
      .filter((u) => !emails.includes(u.email))
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.includes(q))
      .slice(0, TEAM_SUGGESTION_LIMIT)
  }, [users, emails, draft])

  return (
    <div className="en-recipient-block">
      <div className="en-recipient-block__label">
        <span>{label}</span>
        {hint ? <span className="en-recipient-block__hint">{hint}</span> : null}
      </div>

      <div className={`en-chip-box${disabled ? ' is-disabled' : ''}`}>
        {emails.map((email) => {
          const name = nameByEmail.get(email)
          return (
            <span key={email} className={`en-chip${name ? ' en-chip--team' : ''}`} title={email}>
              {name || email}
              {!disabled && (
                <button
                  type="button"
                  className="en-chip__remove"
                  onClick={() => removeEmail(email)}
                  aria-label={`Remove ${email}`}
                >
                  <X size={12} />
                </button>
              )}
            </span>
          )
        })}
        <input
          type="text"
          inputMode="email"
          className="en-chip-input"
          placeholder={emails.length ? 'Add another…' : 'Type an email or a teammate’s name'}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (EMAIL_RE.test(draft.trim())) addEmail(draft)
          }}
          aria-label={`${label} recipients`}
        />
        <button
          type="button"
          className="en-chip-add"
          disabled={disabled || !EMAIL_RE.test(draft.trim())}
          onClick={() => addEmail(draft)}
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      {!disabled && suggestions.length > 0 && (
        <div className="en-quick-users">
          <span className="en-quick-users__label">Team</span>
          {suggestions.map((u) => (
            <button
              key={u._id}
              type="button"
              className="en-quick-user"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onPickUser(u)
                setDraft('')
              }}
              title={u.email}
            >
              {u.name || u.email}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const EmailNotifications = () => {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('events')
  const [canEdit, setCanEdit] = useState(false)
  const [events, setEvents] = useState([])
  const [categories, setCategories] = useState([])
  const [users, setUsers] = useState([])
  const [mailStatus, setMailStatus] = useState(null)
  const [statusLoading, setStatusLoading] = useState(false)

  const [eventFilter, setEventFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [selectedKey, setSelectedKey] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [testMessage, setTestMessage] = useState('')

  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [savingSchedule, setSavingSchedule] = useState(false)
  const [runningNow, setRunningNow] = useState(false)
  const [previewingDigest, setPreviewingDigest] = useState(false)
  const [digestPreview, setDigestPreview] = useState(null)
  const [previewMode, setPreviewMode] = useState('desktop')

  const loadStatus = useCallback(async (refresh = false) => {
    setStatusLoading(true)
    try {
      const res = await emailNotificationService.getStatus({ refresh })
      setMailStatus(res?.data || null)
    } catch (_) {
      setMailStatus(null)
    } finally {
      setStatusLoading(false)
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [configRes, scheduleRes] = await Promise.all([
        emailNotificationService.getConfig(),
        emailNotificationService.getSchedule().catch(() => null),
      ])
      const data = configRes?.data || {}
      const list = Array.isArray(data.events) ? data.events : []
      setEvents(list)
      setCategories(Array.isArray(data.categories) ? data.categories : [])
      setUsers((Array.isArray(data.users) ? data.users : []).filter((u) => EMAIL_RE.test(u.email || '')))
      setCanEdit(Boolean(data.canEdit))
      if (scheduleRes?.data) setSchedule({ ...DEFAULT_SCHEDULE, ...scheduleRes.data })
      setSelectedKey((prev) => (prev && list.some((e) => e.eventKey === prev) ? prev : list[0]?.eventKey || null))
    } catch (err) {
      reportError(err, 'Failed to load email notification settings')
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    loadStatus()
  }, [load, loadStatus])

  const selectedEvent = useMemo(
    () => events.find((e) => e.eventKey === selectedKey) || null,
    [events, selectedKey],
  )

  // Reset the form only when the selection changes, so a list refresh never wipes unsaved picks.
  useEffect(() => {
    setForm(formFromEvent(selectedEvent))
    setTestMessage('')
  }, [selectedKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading && selectedEvent) setForm(formFromEvent(selectedEvent))
  }, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = selectedEvent ? !sameForm(form, formFromEvent(selectedEvent)) : false

  const categoryTabs = useMemo(() => {
    const counts = new Map()
    events.forEach((ev) => counts.set(ev.category || 'Other', (counts.get(ev.category || 'Other') || 0) + 1))
    const order = [...categories, ...[...counts.keys()].filter((c) => !categories.includes(c))]
    return [
      { id: 'all', label: 'All', count: events.length },
      ...order.filter((c) => counts.has(c)).map((c) => ({ id: c, label: categoryLabel(c), count: counts.get(c) })),
    ]
  }, [events, categories])

  const filteredEvents = useMemo(() => {
    const q = eventFilter.trim().toLowerCase()
    return events.filter((ev) => {
      if (categoryFilter !== 'all' && (ev.category || 'Other') !== categoryFilter) return false
      return eventMatchesQuery(ev, q)
    })
  }, [events, eventFilter, categoryFilter])

  const groupedEvents = useMemo(() => {
    const map = new Map()
    filteredEvents.forEach((ev) => {
      const cat = ev.category || 'Other'
      if (!map.has(cat)) map.set(cat, [])
      map.get(cat).push(ev)
    })
    return [...map.entries()]
  }, [filteredEvents])

  const showGroups = categoryFilter === 'all' && !eventFilter.trim()

  useEffect(() => {
    if (!filteredEvents.length) return
    if (selectedKey && filteredEvents.some((e) => e.eventKey === selectedKey)) return
    setSelectedKey(filteredEvents[0].eventKey)
  }, [filteredEvents, selectedKey])

  const selectEvent = (ev) => {
    if (ev.eventKey === selectedKey) return
    if (isDirty && !window.confirm('Discard unsaved recipient changes?')) return
    setSelectedKey(ev.eventKey)
  }

  const pickTeamUser = (fieldIds, fieldEmails) => (user) => {
    const id = String(user._id)
    setForm((f) => ({
      ...f,
      [fieldIds]: f[fieldIds].includes(id) ? f[fieldIds] : [...f[fieldIds], id],
      [fieldEmails]: normalizeList([...f[fieldEmails], user.email]),
    }))
  }

  // Removing a teammate's chip also drops their user id so the backend doesn't re-add them.
  const syncEmailsField = (fieldEmails, fieldIds) => (nextEmails) => {
    const normalized = normalizeList(nextEmails)
    setForm((f) => ({
      ...f,
      [fieldEmails]: normalized,
      [fieldIds]: f[fieldIds].filter((id) => {
        const u = users.find((x) => String(x._id) === String(id))
        return u && normalized.includes(u.email)
      }),
    }))
  }

  const handleSave = async () => {
    if (!selectedKey || !canEdit) return
    setSaving(true)
    try {
      const res = await emailNotificationService.saveRouting(selectedKey, form)
      const saved = res?.data
      if (saved) {
        setEvents((prev) => prev.map((e) => (e.eventKey === selectedKey ? { ...e, ...saved } : e)))
        setForm(formFromEvent(saved))
      }
      if (selectedEvent?.managementEmail || selectedEvent?.category === 'Management MIS') {
        try {
          await emailNotificationService.saveManagementConfig(selectedKey, { enabled: form.enabled })
        } catch (_) { /* schedule panel may not be created yet */ }
      }
      if (selectedEvent?.gstComplianceEmail || selectedEvent?.category === 'GST Compliance') {
        try {
          await emailNotificationService.saveGstComplianceStage(selectedKey, { enabled: form.enabled })
        } catch (_) { /* stage may initialise on first open */ }
      }
      toast.success(res?.message || 'Recipients saved')
    } catch (err) {
      reportError(err, 'Failed to save recipients')
    } finally {
      setSaving(false)
    }
  }

  const handleTestMail = async () => {
    if (!selectedKey || !canEdit) return
    if (!form.toEmails.length && !form.ccEmails.length && !form.bccEmails.length) {
      toast.error('Add at least one To, CC, or BCC address first')
      return
    }
    setTesting(true)
    try {
      const res = await emailNotificationService.sendTest(selectedKey, {
        toEmails: form.toEmails,
        ccEmails: form.ccEmails,
        bccEmails: form.bccEmails,
        message: testMessage,
      })
      toast.success(res?.message || 'Test email sent')
    } catch (err) {
      reportError(err, 'Failed to send test email')
      loadStatus(true)
    } finally {
      setTesting(false)
    }
  }

  const setLeadTime = (key, value) =>
    setSchedule((s) => ({ ...s, leadTimes: { ...s.leadTimes, [key]: value } }))

  const handleSaveSchedule = async () => {
    if (!canEdit) return
    setSavingSchedule(true)
    try {
      const res = await emailNotificationService.saveSchedule({
        timezone: schedule.timezone,
        sendTime: schedule.sendTime,
        digestEnabled: schedule.digestEnabled,
        senderDisplayName: schedule.senderDisplayName,
        senderEmail: schedule.senderEmail,
        replyToName: schedule.replyToName,
        replyToEmail: schedule.replyToEmail,
        hideZeroCategories: schedule.hideZeroCategories,
        supportContact: schedule.supportContact,
        leadTimes: Object.fromEntries(
          LEAD_TIME_FIELDS.map((f) => [f.key, Number(schedule.leadTimes?.[f.key])]),
        ),
      })
      if (res?.data) setSchedule({ ...DEFAULT_SCHEDULE, ...res.data })
      toast.success(res?.message || 'Schedule saved')
    } catch (err) {
      reportError(err, 'Failed to save schedule')
    } finally {
      setSavingSchedule(false)
    }
  }

  const handleRunNow = async () => {
    if (!canEdit) return
    setRunningNow(true)
    try {
      const res = await emailNotificationService.sendDigestNow()
      toast.success(res?.message || 'Daily digest sent')
      emailNotificationService.getSchedule().then((r) => r?.data && setSchedule({ ...DEFAULT_SCHEDULE, ...r.data })).catch(() => {})
    } catch (err) {
      reportError(err, 'Failed to send the daily digest')
      loadStatus(true)
    } finally {
      setRunningNow(false)
    }
  }

  const handlePreviewDigest = async () => {
    if (!canEdit) return
    setPreviewingDigest(true)
    try {
      const res = await emailNotificationService.previewDigest()
      setDigestPreview(res?.data || null)
      toast.success('Digest preview ready')
    } catch (err) {
      reportError(err, 'Failed to preview the daily digest')
    } finally {
      setPreviewingDigest(false)
    }
  }

  const digestEvent = useMemo(() => events.find((e) => e.category === 'Scheduled') || null, [events])
  const digestMissingRecipients = Boolean(digestEvent && digestEvent.enabled !== false && !digestEvent.toEmails?.length)
  const digestSwitchedOff = digestEvent?.enabled === false

  const goToDigestRecipients = () => {
    setActiveTab('events')
    setEventFilter('')
    setCategoryFilter('Scheduled')
    if (digestEvent) setSelectedKey(digestEvent.eventKey)
  }

  const statusChip = (() => {
    if (!mailStatus) return null
    const providerLabel = mailStatus.provider === 'zoho_api' ? 'Zoho Mail' : 'SMTP'
    const ready = mailStatus.ready === true
    const label = ready
      ? `${providerLabel} ready`
      : mailStatus.missing?.length
        ? `${providerLabel} not configured`
        : mailStatus.authError ? `${providerLabel} check failed` : 'Mail not ready'
    const title = ready
      ? `Sending as ${mailStatus.fromEmail || 'configured sender'} via ${providerLabel}`
      : mailStatus.missing?.length
        ? `Missing server settings: ${mailStatus.missing.join(', ')}`
        : mailStatus.authError || 'Set MAIL_SEND_PROVIDER in the server environment'
    return (
      <button
        type="button"
        className={`en-status-chip${ready ? ' is-ready' : ' is-warn'}`}
        title={`${title} — click to re-check`}
        onClick={() => loadStatus(true)}
        disabled={statusLoading}
      >
        {statusLoading
          ? <RefreshCw size={14} className="en-spin" aria-hidden />
          : ready ? <CheckCircle2 size={14} aria-hidden /> : <AlertCircle size={14} aria-hidden />}
        <span>{label}</span>
      </button>
    )
  })()

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Email Notifications', path: '/settings/email-notifications', isCurrent: true },
  ]

  const renderEventButton = (ev, showCategory) => {
    const copies = recipientCount(ev)
    return (
      <button
        key={ev.eventKey}
        type="button"
        className={`en-event-btn${selectedKey === ev.eventKey ? ' is-active' : ''}`}
        onClick={() => selectEvent(ev)}
      >
        <span className="en-event-btn__title">{ev.label}</span>
        {showCategory ? <span className="en-event-btn__cat">{categoryLabel(ev.category)}</span> : null}
        {ev.enabled === false
          ? <span className="en-event-btn__off">Off</span>
          : copies > 0 ? <span className="en-event-btn__count" title={`${copies} extra recipient${copies === 1 ? '' : 's'}`}>{copies}</span> : null}
      </button>
    )
  }

  const scheduleTab = (
    <div className="en-schedule">
      <header className="en-schedule-hero">
        <div className="en-schedule-hero__glow" aria-hidden />
        <div className="en-schedule-hero__main">
          <p className="en-schedule-hero__kicker">Morning digest</p>
          <div className="en-schedule-hero__time-row">
            <span className="en-schedule-hero__time">{schedule.sendTime}</span>
            <span className="en-schedule-hero__tz">
              <Globe2 size={14} strokeWidth={1.75} aria-hidden />
              {schedule.timezone}
            </span>
          </div>
          <p className="en-schedule-hero__lede">
            One summary each morning: approvals waiting, maintenance and tasks due, contracts renewing, and quotations going stale.
          </p>
        </div>
        <div className="en-schedule-hero__stat">
          <strong>{schedule.digestEnabled && !digestSwitchedOff ? 'On' : 'Off'}</strong>
          <em>
            {schedule.lastDigestAt
              ? `Last run ${formatDateTime(schedule.lastDigestAt)}${schedule.lastDigestStatus ? ` · ${schedule.lastDigestStatus}` : ''}`
              : 'Not sent yet'}
          </em>
        </div>
      </header>

      <div className="en-schedule-shell">
        <section className="en-schedule-block">
          <div className="en-schedule-block__head">
            <Clock size={16} strokeWidth={1.75} aria-hidden />
            <h3>Send window</h3>
          </div>
          <div className="en-schedule-timing">
            <label className="en-schedule-timing__field">
              <span>Local time</span>
              <input
                type="time"
                className="en-schedule-timing__input"
                value={schedule.sendTime}
                disabled={!canEdit}
                onChange={(e) => setSchedule((s) => ({ ...s, sendTime: e.target.value || '08:00' }))}
              />
            </label>
            <label className="en-schedule-timing__field en-schedule-timing__field--grow">
              <span>Timezone</span>
              <input
                className="en-schedule-timing__input"
                list="en-tz-list"
                value={schedule.timezone}
                disabled={!canEdit}
                onChange={(e) => setSchedule((s) => ({ ...s, timezone: e.target.value }))}
                placeholder="Asia/Kolkata"
              />
              <datalist id="en-tz-list">
                {TIMEZONES.map((tz) => <option key={tz} value={tz} />)}
              </datalist>
            </label>
          </div>
        </section>

        <section className="en-schedule-block">
          <div className="en-schedule-block__head">
            <CalendarClock size={16} strokeWidth={1.75} aria-hidden />
            <h3>Lead times</h3>
          </div>
          <div className="en-schedule-metrics">
            {LEAD_TIME_FIELDS.map((m) => (
              <label key={m.key} className="en-schedule-metric">
                <span className="en-schedule-metric__label">{m.label}</span>
                <input
                  type="number"
                  min={m.min}
                  max={m.max}
                  className="en-schedule-metric__value"
                  value={schedule.leadTimes?.[m.key] ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => setLeadTime(m.key, e.target.value)}
                />
                <span className="en-schedule-metric__suffix">{m.suffix}</span>
              </label>
            ))}
          </div>
          <p className="en-callout en-callout--muted">
            <Info size={14} aria-hidden />
            Contracts are listed when they renew within 30 days.
          </p>
        </section>

        <section className="en-schedule-block">
          <div className="en-schedule-block__head">
            <Mail size={16} strokeWidth={1.75} aria-hidden />
            <h3>Daily digest</h3>
            <button type="button" className="en-link-btn" onClick={goToDigestRecipients}>
              Set recipients
            </button>
          </div>
          {digestSwitchedOff && (
            <p className="en-callout en-callout--warn">
              <AlertCircle size={14} aria-hidden />
              The digest is switched off under Event recipients, so neither the schedule nor Send now will deliver it.
            </p>
          )}
          {digestMissingRecipients && (
            <p className="en-callout en-callout--warn">
              <AlertCircle size={14} aria-hidden />
              Add at least one To address for the digest. It will not send until recipients are set.
            </p>
          )}
          {schedule.lastDigestStatus === 'failed' && schedule.lastDigestError && (
            <p className="en-callout en-callout--warn">
              <AlertCircle size={14} aria-hidden />
              Last scheduled run failed: {schedule.lastDigestError}
            </p>
          )}
          <ul className="en-schedule-jobs">
            {(schedule.scheduledEvents?.length ? schedule.scheduledEvents : [{ key: 'digest', label: 'Daily ERP digest', description: '' }]).map((ev) => (
              <li key={ev.key}>
                <button
                  type="button"
                  className={`en-schedule-job${schedule.digestEnabled ? ' is-on' : ''}`}
                  disabled={!canEdit}
                  onClick={() => setSchedule((s) => ({ ...s, digestEnabled: !s.digestEnabled }))}
                  aria-pressed={schedule.digestEnabled}
                >
                  <span className="en-schedule-job__copy">
                    <strong>{ev.label}</strong>
                    {ev.description ? <span>{ev.description}</span> : null}
                  </span>
                  <span className={`en-switch${schedule.digestEnabled ? ' is-on' : ''}`} aria-hidden><i /></span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="en-schedule-block">
          <div className="en-schedule-block__head">
            <Mail size={16} strokeWidth={1.75} aria-hidden />
            <h3>Sender identity</h3>
          </div>
          <p className="en-callout" style={{ marginBottom: '0.75rem' }}>
            Recipients see this display name. The authenticated mailbox address stays unchanged.
          </p>
          <div className="en-schedule-timing" style={{ flexWrap: 'wrap' }}>
            <label className="en-schedule-timing__field en-schedule-timing__field--grow">
              <span>Sender display name</span>
              <input
                className="en-schedule-timing__input"
                value={schedule.senderDisplayName || ''}
                disabled={!canEdit}
                onChange={(e) => setSchedule((s) => ({ ...s, senderDisplayName: e.target.value }))}
                placeholder="Celeris ERP Notifications"
              />
            </label>
            <label className="en-schedule-timing__field en-schedule-timing__field--grow">
              <span>Sender email (optional override)</span>
              <input
                className="en-schedule-timing__input"
                value={schedule.senderEmail || ''}
                disabled={!canEdit}
                onChange={(e) => setSchedule((s) => ({ ...s, senderEmail: e.target.value }))}
                placeholder="Uses authenticated mailbox when blank"
              />
            </label>
            <label className="en-schedule-timing__field en-schedule-timing__field--grow">
              <span>Reply-to name</span>
              <input
                className="en-schedule-timing__input"
                value={schedule.replyToName || ''}
                disabled={!canEdit}
                onChange={(e) => setSchedule((s) => ({ ...s, replyToName: e.target.value }))}
              />
            </label>
            <label className="en-schedule-timing__field en-schedule-timing__field--grow">
              <span>Reply-to email</span>
              <input
                className="en-schedule-timing__input"
                value={schedule.replyToEmail || ''}
                disabled={!canEdit}
                onChange={(e) => setSchedule((s) => ({ ...s, replyToEmail: e.target.value }))}
              />
            </label>
            <label className="en-schedule-timing__field en-schedule-timing__field--grow">
              <span>Support contact (footer)</span>
              <input
                className="en-schedule-timing__input"
                value={schedule.supportContact || ''}
                disabled={!canEdit}
                onChange={(e) => setSchedule((s) => ({ ...s, supportContact: e.target.value }))}
                placeholder="support@company.com"
              />
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.75rem', fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              checked={schedule.hideZeroCategories !== false}
              disabled={!canEdit}
              onChange={(e) => setSchedule((s) => ({ ...s, hideZeroCategories: e.target.checked }))}
            />
            Hide zero-value digest categories (show one compact “no pending” line)
          </label>
        </section>

        <footer className="en-schedule-footer">
          <button type="button" className="en-btn en-btn--primary" onClick={handleSaveSchedule} disabled={!canEdit || savingSchedule}>
            <Save size={16} strokeWidth={1.75} aria-hidden />
            {savingSchedule ? 'Saving…' : 'Save schedule'}
          </button>
          <button
            type="button"
            className="en-btn en-btn--secondary"
            onClick={handlePreviewDigest}
            disabled={!canEdit || previewingDigest}
            title="Preview the fully rendered Daily Digest"
          >
            <Mail size={16} strokeWidth={1.75} aria-hidden />
            {previewingDigest ? 'Previewing…' : 'Preview digest'}
          </button>
          <button
            type="button"
            className="en-btn en-btn--secondary"
            onClick={handleRunNow}
            disabled={!canEdit || runningNow}
            title="Build today’s digest and send it to the digest recipients now"
          >
            <Send size={16} strokeWidth={1.75} aria-hidden />
            {runningNow ? 'Sending…' : 'Send now'}
          </button>
        </footer>

        {digestPreview ? (
          <section className="en-schedule-block" style={{ marginTop: '1rem' }}>
            <div className="en-schedule-block__head">
              <Mail size={16} strokeWidth={1.75} aria-hidden />
              <h3>Digest preview</h3>
            </div>
            <div style={{ fontSize: '0.8rem', lineHeight: 1.5, marginBottom: '0.75rem' }}>
              <div><strong>From:</strong> {digestPreview.fromName} &lt;{digestPreview.fromEmail}&gt;</div>
              {digestPreview.replyTo ? <div><strong>Reply-to:</strong> {digestPreview.replyTo}</div> : null}
              <div><strong>Subject:</strong> {digestPreview.subject}</div>
              <div><strong>To:</strong> {(digestPreview.to || []).join(', ') || '—'}</div>
              <div><strong>CC:</strong> {(digestPreview.cc || []).join(', ') || '—'}</div>
              <div><strong>BCC:</strong> {(digestPreview.bcc || []).join(', ') || '—'}</div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button type="button" className={`en-btn ${previewMode === 'desktop' ? 'en-btn--primary' : 'en-btn--secondary'}`} onClick={() => setPreviewMode('desktop')}>Desktop</button>
              <button type="button" className={`en-btn ${previewMode === 'mobile' ? 'en-btn--primary' : 'en-btn--secondary'}`} onClick={() => setPreviewMode('mobile')}>Mobile</button>
            </div>
            <div
              style={{
                maxWidth: previewMode === 'mobile' ? 380 : 640,
                margin: '0 auto',
                border: '1px solid #E2E8F0',
                borderRadius: 8,
                overflow: 'auto',
                maxHeight: 520,
                background: '#F1F5F9',
              }}
              dangerouslySetInnerHTML={{ __html: digestPreview.html || '' }}
            />
          </section>
        ) : null}
      </div>
    </div>
  )

  const eventsTab = events.length === 0 ? (
    <div className="en-empty">
      <p className="en-empty__title">No notification events</p>
      <p className="en-empty__hint">The event catalog could not be loaded. Try refreshing the page.</p>
    </div>
  ) : (
    <div className="en-layout">
      <aside className="en-events">
        <div className="en-events__toolbar">
          <div className="en-events__chips" role="tablist" aria-label="Event category">
            {categoryTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={categoryFilter === tab.id}
                className={`en-filter-chip${categoryFilter === tab.id ? ' is-active' : ''}`}
                onClick={() => setCategoryFilter(tab.id)}
              >
                {tab.label}
                <em>{tab.count}</em>
              </button>
            ))}
          </div>
          <div className="en-events__search">
            <Search size={14} className="en-events__search-icon" aria-hidden />
            <input
              type="search"
              className="en-input"
              placeholder="Search events — e.g. claim, asset, renewal"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              aria-label="Search events"
            />
            {eventFilter ? (
              <button type="button" className="en-events__clear" onClick={() => setEventFilter('')} aria-label="Clear search">
                <X size={14} />
              </button>
            ) : null}
          </div>
          <p className="en-events__count">
            {filteredEvents.length === 0
              ? 'No matches'
              : `${filteredEvents.length} ${eventFilter.trim() ? `match${filteredEvents.length === 1 ? '' : 'es'}` : `event${filteredEvents.length === 1 ? '' : 's'}`}`}
          </p>
        </div>

        <div className="en-events__list">
          {filteredEvents.length === 0 ? (
            <p className="en-events__empty">Nothing matches. Try another word or pick a different category.</p>
          ) : showGroups ? (
            groupedEvents.map(([category, items]) => (
              <div key={category}>
                <div className="en-events__group-label">{categoryLabel(category)}</div>
                {items.map((ev) => renderEventButton(ev, false))}
              </div>
            ))
          ) : (
            filteredEvents.map((ev) => renderEventButton(ev, categoryFilter === 'all'))
          )}
        </div>
      </aside>

      <div className="en-panel">
        {selectedEvent ? (
          <>
            <div className="en-panel__header">
              <div>
                <p className="en-panel__eyebrow">{categoryLabel(selectedEvent.category)}</p>
                <h2 className="en-panel__title">{selectedEvent.label}</h2>
                <p className="en-panel__desc">{selectedEvent.description}</p>
              </div>
              <label className={`en-toggle${form.enabled ? ' is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={form.enabled}
                  disabled={!canEdit}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
                <span className={`en-switch${form.enabled ? ' is-on' : ''}`} aria-hidden><i /></span>
                <span className="en-toggle__label">{form.enabled ? 'On' : 'Off'}</span>
              </label>
            </div>

            {selectedEvent.ownerNote ? (
              <p className="en-callout">
                <Mail size={14} aria-hidden />
                {selectedEvent.ownerNote}{' '}
                {selectedEvent.scheduled ? '' : 'Add To / CC / BCC only for extra copies.'}
              </p>
            ) : null}

            {!form.enabled && (
              <p className="en-callout en-callout--warn">
                <AlertCircle size={14} aria-hidden />
                This event is off. Nobody will be emailed for it, including the person on the record.
              </p>
            )}

            {selectedEvent.managementEmail || selectedEvent.category === 'Management MIS' ? (
              <ManagementEmailSettingsPanel
                eventKey={selectedEvent.eventKey}
                canEdit={canEdit}
                users={users}
              />
            ) : null}

            {selectedEvent.gstComplianceEmail || selectedEvent.category === 'GST Compliance' ? (
              <GstComplianceEmailPanel
                eventKey={selectedEvent.eventKey}
                canEdit={canEdit}
              />
            ) : null}

            <EmailChipList
              label="To"
              hint="Primary extra recipients"
              emails={form.toEmails}
              onChange={syncEmailsField('toEmails', 'toUserIds')}
              onPickUser={pickTeamUser('toUserIds', 'toEmails')}
              disabled={!canEdit || !form.enabled}
              users={users}
            />
            <EmailChipList
              label="CC"
              hint="Visible copies"
              emails={form.ccEmails}
              onChange={syncEmailsField('ccEmails', 'ccUserIds')}
              onPickUser={pickTeamUser('ccUserIds', 'ccEmails')}
              disabled={!canEdit || !form.enabled}
              users={users}
            />
            <EmailChipList
              label="BCC"
              hint="Hidden copies"
              emails={form.bccEmails}
              onChange={syncEmailsField('bccEmails', 'bccUserIds')}
              onPickUser={pickTeamUser('bccUserIds', 'bccEmails')}
              disabled={!canEdit || !form.enabled}
              users={users}
            />

            <details className="en-test-details">
              <summary>Send a test email</summary>
              <div className="en-test-box">
                <label className="en-test-box__label" htmlFor="en-test-msg">Optional note</label>
                <textarea
                  id="en-test-msg"
                  className="en-input"
                  rows={2}
                  maxLength={1000}
                  value={testMessage}
                  disabled={!canEdit}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="Included in the test email body"
                />
                <p className="en-test-box__hint">Goes to the To / CC / BCC shown above, including unsaved changes.</p>
                <button
                  type="button"
                  className="en-btn en-btn--secondary"
                  onClick={handleTestMail}
                  disabled={!canEdit || testing || !form.enabled}
                >
                  <Send size={16} strokeWidth={1.75} aria-hidden />
                  {testing ? 'Sending…' : 'Send test email'}
                </button>
              </div>
            </details>

            <div className="en-actions">
              <button type="button" className="en-btn en-btn--primary" onClick={handleSave} disabled={!canEdit || saving || !isDirty}>
                <Save size={16} strokeWidth={1.75} aria-hidden />
                {saving ? 'Saving…' : 'Save recipients'}
              </button>
              {(selectedEvent.scheduled || selectedEvent.category === 'Scheduled')
                && !selectedEvent.managementEmail
                && selectedEvent.category !== 'Management MIS'
                && !selectedEvent.gstComplianceEmail
                && selectedEvent.category !== 'GST Compliance' ? (
                <button
                  type="button"
                  className="en-btn en-btn--secondary"
                  onClick={handleRunNow}
                  disabled={
                    !canEdit
                    || runningNow
                    || !form.enabled
                    || isDirty
                    || !form.toEmails.length
                  }
                  title={
                    isDirty
                      ? 'Save recipients first, then send'
                      : !form.toEmails.length
                        ? 'Add at least one To address before sending'
                        : !form.enabled
                          ? 'Turn the digest on before sending'
                          : 'Build today’s digest and email it to the saved To / CC / BCC now'
                  }
                >
                  <Send size={16} strokeWidth={1.75} aria-hidden />
                  {runningNow ? 'Sending…' : 'Send now'}
                </button>
              ) : null}
              {isDirty ? <span className="en-actions__dirty">Unsaved changes</span> : null}
              {!isDirty && selectedEvent.updatedAt ? (
                <span className="en-actions__meta">Last saved {formatDateTime(selectedEvent.updatedAt)}</span>
              ) : null}
            </div>
          </>
        ) : (
          <div className="en-empty en-empty--flat">
            <p className="en-empty__title">Select an event</p>
            <p className="en-empty__hint">Choose an event on the left to set To, CC, and BCC.</p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="sales-dashboard en-page">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Email Notifications</h1>
          <p>Choose who gets emailed for each ERP event, and when the daily digest goes out</p>
        </div>
      </div>

      {!loading && !canEdit && (
        <div className="en-alert" role="status">
          <Info size={15} aria-hidden />
          View only. Administrators can change these settings.
        </div>
      )}

      <div className="en-toolbar">
        <div className="en-tabs" role="tablist" aria-label="Email notification sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'events'}
            className={`en-tabs__btn${activeTab === 'events' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            <Users size={15} strokeWidth={1.75} aria-hidden />
            Event recipients
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'schedule'}
            className={`en-tabs__btn${activeTab === 'schedule' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            <CalendarClock size={15} strokeWidth={1.75} aria-hidden />
            Daily schedule
          </button>
        </div>
        {statusChip}
      </div>

      {loading ? <p className="en-loading">Loading…</p> : activeTab === 'schedule' ? scheduleTab : eventsTab}
    </div>
  )
}

export default EmailNotifications
